import { create } from 'zustand';
import { GameMode } from '@elmental/shared';
import { isAudioEnabled, setAudioEnabled } from '../services/audio';
import {
  loadOpponentStats,
  recordOpponentMatch,
  saveOpponentStats,
  type OpponentMatchOutcome,
  type OpponentStats,
} from '../services/opponentStats';
import { runScreenTransition, type ScreenTransitionDirection } from '../services/screenTransition';
import type { WalletHistoryEntry, WalletHistorySummary } from '../services/payments';

export type Screen =
  | 'home'
  | 'matchmaking'
  | 'match'
  | 'result'
  | 'profile'
  | 'information'
  | 'settings';

export type MatchStatus = 'idle' | 'queuing' | 'playing' | 'result';
export type RoundPhase = 'select' | 'commit' | 'reveal' | 'result';
export type EnergyLevel = 'low' | 'medium' | 'high';

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  initData?: string;
  source: 'telegram' | 'web';
}

export interface MatchResult {
  winner: 'me' | 'opponent' | 'draw';
  balanceKind: string;
  myScore: number;
  opponentScore: number;
  elmSpent: number;
  elmDelta: number;
  seasonPointsEarned: number;
  ratingChange: number;
  rounds: RoundEntry[];
  // Economy breakdown
  entryFee: number;
  boostCost: number;
}

export interface EconomyTransaction {
  id: string;
  type:
    | 'entry_fee'
    | 'boost_cost'
    | 'stake'
    | 'win'
    | 'loss'
    | 'boost_burn'
    | 'boost_return'
    | 'rake';
  amount: number;
  matchId: string;
  timestamp: number;
  description: string;
}

export interface RoundEntry {
  round: number;
  myMove: number;
  opponentMove: number;
  result: 'win' | 'lose' | 'draw';
  myEnergyBefore: number;
  myEnergyAfter: number;
}

export interface LastRoundResult {
  myMove: number;
  opponentMove: number;
  result: 'win' | 'lose' | 'draw';
  myEnergyBefore: number;
  myEnergyAfter: number;
  opponentEnergyLevel: EnergyLevel;
  wasOverclocked: boolean;
}

interface GameStore {
  // Navigation
  currentScreen: Screen;
  setScreen: (screen: Screen) => void;

  // Telegram user
  telegramUser: TelegramUser | null;
  setTelegramUser: (user: TelegramUser) => void;

  // Player stats
  elmBalance: number;
  seasonPoints: number;
  rating: number;
  stats: { wins: number; losses: number };
  opponentStats: OpponentStats[];
  setPlayerStats: (stats: { elmBalance: number; rating: number; wins: number; losses: number; seasonPoints?: number }) => void;
  recordOpponentResult: (result: {
    opponentName: string;
    winner: OpponentMatchOutcome;
    myScore: number;
    opponentScore: number;
  }) => void;

  // Game settings
  gameMode: GameMode;
  boostEnabled: boolean;
  setGameMode: (mode: GameMode) => void;
  setBoostEnabled: (enabled: boolean) => void;
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;

  // Economy
  transactions: EconomyTransaction[];
  walletHistory: WalletHistoryEntry[];
  walletHistorySummary: WalletHistorySummary | null;
  walletHistoryStatus: 'idle' | 'loading' | 'ready' | 'failed';
  matchStake: number;
  matchBoostStake: number;
  addTransaction: (tx: EconomyTransaction) => void;
  setWalletHistory: (
    entries: WalletHistoryEntry[],
    summary: WalletHistorySummary | null,
    status: 'idle' | 'loading' | 'ready' | 'failed',
  ) => void;

  // Match state
  matchId: string | null;
  matchBalanceKind: string;
  isPlayer1: boolean;
  matchStatus: MatchStatus;
  opponentName: string;
  opponentRating: number;
  myEnergy: number;
  opponentEnergyLevel: EnergyLevel;
  myScore: number;
  opponentScore: number;
  currentRound: number;
  totalRounds: number;
  roundPhase: RoundPhase;
  selectedMove: number | null;
  roundTimer: number;
  lastRoundResult: LastRoundResult | null;
  matchResult: MatchResult | null;
  roundHistory: RoundEntry[];

  // Match actions
  setMatchStatus: (status: MatchStatus) => void;
  startMatchmaking: () => void;
  cancelMatchmaking: () => void;
  setMatchFound: (
    matchId: string,
    balanceKind: string,
    opponentName: string,
    opponentRating: number,
    isPlayer1?: boolean,
  ) => void;
  selectMove: (moveId: number) => void;
  setRoundPhase: (phase: RoundPhase) => void;
  updateEnergy: (energy: number) => void;
  updateOpponentEnergyLevel: (level: EnergyLevel) => void;
  updateScores: (myScore: number, opponentScore: number) => void;
  advanceRound: () => void;
  setRoundTimer: (seconds: number) => void;
  setLastRoundResult: (result: LastRoundResult) => void;
  setMatchResult: (result: MatchResult) => void;
  resetMatch: () => void;
}

const DEFAULT_ENERGY = 100;
const SCREEN_FLOW: Screen[] = ['home', 'profile', 'information', 'settings', 'matchmaking', 'match', 'result'];

type StoreSet = (partial: Partial<GameStore> | ((state: GameStore) => Partial<GameStore>)) => void;
type StoreGet = () => GameStore;

function screenTransitionDirection(from: Screen, to: Screen): ScreenTransitionDirection {
  if (from === to) return 'replace';
  if (to === 'home') return 'backward';
  if (from === 'home') return 'forward';

  const fromIndex = SCREEN_FLOW.indexOf(from);
  const toIndex = SCREEN_FLOW.indexOf(to);
  if (fromIndex === -1 || toIndex === -1) return 'replace';

  return toIndex >= fromIndex ? 'forward' : 'backward';
}

function transitionToScreen(
  set: StoreSet,
  get: StoreGet,
  screen: Screen,
  patch: Partial<GameStore> = {},
): void {
  const from = get().currentScreen;
  const nextState = { ...patch, currentScreen: screen };

  if (from === screen) {
    set(nextState);
    return;
  }

  runScreenTransition(() => {
    set(nextState);
  }, screenTransitionDirection(from, screen));
}

export const useGameStore = create<GameStore>((set, get) => ({
  // Navigation
  currentScreen: 'home',
  setScreen: (screen) => transitionToScreen(set, get, screen),

  // Telegram user
  telegramUser: null,
  setTelegramUser: (user) => set({ telegramUser: user }),

  // Player stats
  elmBalance: 0,
  seasonPoints: 0,
  rating: 1200,
  stats: { wins: 0, losses: 0 },
  opponentStats: loadOpponentStats(),
  setPlayerStats: ({ elmBalance, rating, wins, losses, seasonPoints }) =>
    set((state) => ({
      elmBalance,
      rating,
      seasonPoints: seasonPoints ?? state.seasonPoints,
      stats: { wins, losses },
    })),
  recordOpponentResult: (result) =>
    set((state) => {
      const opponentStats = recordOpponentMatch(state.opponentStats, result);
      saveOpponentStats(opponentStats);
      return { opponentStats };
    }),

  // Game settings
  gameMode: GameMode.Classic,
  boostEnabled: false,
  setGameMode: (mode) => set({ gameMode: mode }),
  setBoostEnabled: (enabled) => set({ boostEnabled: enabled }),
  soundEnabled: isAudioEnabled(),
  setSoundEnabled: (enabled) => {
    setAudioEnabled(enabled);
    set({ soundEnabled: enabled });
  },

  // Economy
  transactions: [],
  walletHistory: [],
  walletHistorySummary: null,
  walletHistoryStatus: 'idle',
  matchStake: 0,
  matchBoostStake: 0,
  addTransaction: (tx) => set((state) => ({ transactions: [tx, ...state.transactions].slice(0, 50) })),
  setWalletHistory: (entries, summary, status) => set({
    walletHistory: entries.slice(0, 80),
    walletHistorySummary: summary,
    walletHistoryStatus: status,
  }),

  // Match state
  matchId: null,
  matchBalanceKind: 'demo_teml',
  isPlayer1: true,
  matchStatus: 'idle',
  opponentName: '',
  opponentRating: 1200,
  myEnergy: DEFAULT_ENERGY,
  opponentEnergyLevel: 'high',
  myScore: 0,
  opponentScore: 0,
  currentRound: 1,
  totalRounds: 5,
  roundPhase: 'select',
  selectedMove: null,
  roundTimer: 15,
  lastRoundResult: null,
  matchResult: null,
  roundHistory: [],

  // Match actions
  setMatchStatus: (status) => set({ matchStatus: status }),

  startMatchmaking: () =>
    transitionToScreen(set, get, 'matchmaking', { matchStatus: 'queuing' }),

  cancelMatchmaking: () =>
    transitionToScreen(set, get, 'home', { matchStatus: 'idle' }),

  setMatchFound: (matchId, balanceKind, opponentName, opponentRating, isPlayer1 = true) =>
    transitionToScreen(set, get, 'match', {
      matchId,
      matchBalanceKind: balanceKind,
      isPlayer1,
      opponentName,
      opponentRating,
      matchStatus: 'playing',
      myEnergy: get().boostEnabled ? 120 : DEFAULT_ENERGY,
      opponentEnergyLevel: 'high',
      myScore: 0,
      opponentScore: 0,
      currentRound: 1,
      roundPhase: 'select',
      selectedMove: null,
      roundTimer: 15,
      lastRoundResult: null,
      roundHistory: [],
    }),

  selectMove: (moveId) => set({ selectedMove: moveId, roundPhase: 'commit' }),

  setRoundPhase: (phase) => set({ roundPhase: phase }),

  updateEnergy: (energy) => set({ myEnergy: energy }),

  updateOpponentEnergyLevel: (level) => set({ opponentEnergyLevel: level }),

  updateScores: (myScore, opponentScore) => set({ myScore, opponentScore }),

  advanceRound: () =>
    set((state) => ({
      currentRound: state.currentRound + 1,
      roundPhase: 'select',
      selectedMove: null,
      roundTimer: 15,
    })),

  setRoundTimer: (seconds) => set({ roundTimer: seconds }),

  setLastRoundResult: (result) => {
    const state = get();
    set({
      lastRoundResult: result,
      roundHistory: [
        ...state.roundHistory,
        {
          round: state.currentRound,
          myMove: result.myMove,
          opponentMove: result.opponentMove,
          result: result.result,
          myEnergyBefore: result.myEnergyBefore,
          myEnergyAfter: result.myEnergyAfter,
        },
      ],
    });
  },

  setMatchResult: (result) =>
    transitionToScreen(set, get, 'result', {
      matchResult: result,
      matchStatus: 'result',
      myScore: result.myScore,
      opponentScore: result.opponentScore,
      roundPhase: 'result',
      selectedMove: null,
      roundTimer: 0,
    }),

  resetMatch: () =>
    set({
      matchId: null,
      matchBalanceKind: 'demo_teml',
      isPlayer1: true,
      matchStatus: 'idle',
      opponentName: '',
      opponentRating: 1200,
      myEnergy: DEFAULT_ENERGY,
      opponentEnergyLevel: 'high',
      myScore: 0,
      opponentScore: 0,
      currentRound: 1,
      roundPhase: 'select',
      selectedMove: null,
      roundTimer: 15,
      lastRoundResult: null,
      matchResult: null,
      roundHistory: [],
    }),
}));
